import React, { useCallback, useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import styles from "./EditAccessableDomain.module.css";
import { createStyles, createTheme, makeStyles, TextField, ThemeProvider } from "@material-ui/core";
import { isEmpty } from "../../../utils/ObjectUtils";
import PuiTypography from "../../../components/elements/PuiTypography";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Close";
import { useDispatch, useSelector } from "react-redux";
import {
    GetAccessableEmailDomainList,
    GetAccessableEmailList,
    UpdateAccessableEmailDomainList,
    UpdateAccessableEmailList,
} from "../../../user/UserActions";
import { AppState } from "../../../root/RootReducer";
import { AccessableEmail, AccessableEmailDomain } from "../../../user/User";
import { useInitFlag } from "../../../components/function/useInitFlag";
import { useInitText } from "../../../components/function/useInitText";
import PuiMessage from "../../../components/widgets/PuiMessage";
import { setMessageArgs } from "../../../utils/LogicUtils";
import { MSG_INPUT_REQUEST_INPUT } from "../../../utils/Messages";
import { t } from "i18next";

interface EditAccessableDomainProps {
    setIsSaved: (isSaved: boolean) => void;
    isSaved: boolean;
}

const theme = createTheme({
    palette: {
        secondary: {
            main: "#16ADC1",
        },
    },
});

const useStyles = makeStyles(() =>
    createStyles({
        textfield: {
            width: "100%",
            height: "40px",
            "& .MuiInputBase-root": {
                color: "var(--loading-bg)",
                "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--loading-bg)",
                },
            },
        },
        addButton: {
            marginLeft: "3%",
            paddingLeft: "10px",
            height: "54px",
            fontFamily: "noto-sans-cjk-jp,sans-serif",
            fontSize: "16px",
            width: "130px",
        },
        updateButton: {
            position: "absolute",
            height: "35px",
            width: "130px",
            bottom: "5px",
            right: "30px",
            border: "1px solid rgba(0, 0, 0, 0.12)",
        },
        icon: {
            width: "25px",
            height: "25px",
            fontSize: "16px",
            cursor: "pointer",
            marginLeft: "8px",
            color: "var(--loading-bg)",
        },
        deleteIcon: {
            width: "25px",
            height: "25px",
            cursor: "pointer",
            color: "var(--gary-42)",
        },
    })
);

export default function EditAccessableDomain({ setIsSaved, isSaved }: EditAccessableDomainProps) {
    const classes = useStyles();
    const dispatch = useDispatch();

    // ドメインフォーマットチェック
    const validDomain = new RegExp("^(?!-)[A-Za-z0-9-]+([\\-\\.]{1}[a-z0-9]+)*\\.[A-Za-z]{2,6}$");
    // メールフォーマットチェック
    const validEmail = new RegExp("^[a-zA-Z0-9._:$!%-]+@[a-zA-Z0-9.-]+.[a-zA-Z]$");

    // バックエンドからユーザデータ取得
    const { domainList, emailList, userInfo } = useSelector((state: AppState) => ({
        domainList: state.user.domainList,
        emailList: state.user.emailList,
        userInfo: state.user.userInfo,
    }));

    // 入力中ドメイン名値
    const [domainName, setDomainName] = useState("");
    // 入力中EMAIL値
    const [exceptionMail, setExceptionMail] = useState("");
    // ドメインリストに追加ボタン活性・非活性フラグ
    const [isDomainAddDisabled, setDomainAddDisabled] = useInitFlag(true);
    // EMAILリストに追加ボタン活性・非活性フラグ
    const [isExceptionAddDisabled, setExceptionAddDisabled] = useInitFlag(true);
    // 許可するドメインリスト
    const [currentDomainList, setCurrentDomainList] = useState<AccessableEmailDomain[]>([]);
    // 許可する例外メールリスト
    const [currentEmailList, setCurrentEmailList] = useState<AccessableEmail[]>([]);
    // ドメインリスト編集中Index
    const [currentEditDomain, setCurrentEditDomain] = useInitText();
    // ドメインリスト編集中Index
    const [currentEditEmail, setCurrentEditEmail] = useInitText();
    //　ドメインリスト変更フラグ
    const [isDomainListUpdate, setDomainListUpdate] = useInitFlag();
    // EMAILリスト変更フラグ
    const [isEmailListUpdate, setEmailListUpdate] = useInitFlag();
    // エラーメッセージ
    const [errorMsg, setErrorMsg] = useInitText();
    // 更新メッセージ
    const [successMsg, setSuccessMsg] = useInitText();
    //編集キャンセルボタン表示
    const [isVisibleDomainDel, setIsVisibleDomainDel] = useInitFlag(false);
    const [isVisibleEmailDel, setIsVisibleEmailDel] = useInitFlag(false);

    // 初期化
    const ResetInput = () => {
        setDomainName("");
        setExceptionMail("");
        setCurrentEditDomain("");
        setCurrentEditEmail("");
        setDomainListUpdate(false);
        setEmailListUpdate(false);
    };

    // ドメインリストとEMAILリストを取得する。
    useEffect(() => {
        setCurrentDomainList([]);
        setCurrentEmailList([]);
        dispatch(GetAccessableEmailDomainList());
        dispatch(GetAccessableEmailList());
        // eslint-disable-next-line
    }, []);

    // リスト変更する場合、保存フラグを変更
    useEffect(() => {
        if (isDomainListUpdate || isEmailListUpdate) {
            if (isSaved) {
                setIsSaved(false);
            }
        } else {
            if (!isSaved) {
                setIsSaved(true);
            }
        }
        // eslint-disable-next-line
    }, [isDomainListUpdate, isEmailListUpdate]);

    // エラーバー消去
    const closeErrorMsg = () => {
        setErrorMsg("");
    };

    // エラーバー消去
    const closeSuccessMsg = () => {
        setSuccessMsg("");
    };

    // バックエンドからのドメインリストを設定する。
    useEffect(() => {
        setCurrentDomainList(domainList);
    }, [domainList]);

    // バックエンドからのEMAILリストを設定する。
    useEffect(() => {
        setCurrentEmailList(emailList);
    }, [emailList]);

    // 入力した値をドメイン名に設定する。
    const handleDomainChange = (event: any) => {
        setDomainName(event.target.value);
    };

    // 追加ボタン活性・非活性変更
    useEffect(() => {
        if (isEmpty(domainName)) {
            setDomainAddDisabled(true);
        } else {
            setDomainAddDisabled(false);
        }
        // eslint-disable-next-line
    }, [domainName]);

    // ドメイン追加ボタン処理
    const handleClickDomainAdd = useCallback(() => {
        if (!validDomain.test(domainName)) {
            setErrorMsg(t("入力したドメインが正しくありません。"));
        } else {
            const domainNameList =
                currentDomainList.length > 0
                    ? currentDomainList.map((item) => item.emailDomain)
                    : [];
            if (currentEditDomain === "") {
                if (!isDuplicate(domainName, domainNameList, false)) {
                    currentDomainList.push({
                        id: -1,
                        emailDomain: domainName,
                    });
                    setDomainName("");
                    if (!isDomainListUpdate) {
                        setDomainListUpdate(true);
                    }
                } else {
                    setErrorMsg(t("このドメインが重複しています。"));
                }
            } else {
                var newList = [...currentDomainList];
                const index = newList.findIndex((item) => item.emailDomain === currentEditDomain);

                if (!isDuplicate(domainName, domainNameList, true, index)) {
                    newList[index] = { ...newList[index], emailDomain: domainName };
                    setCurrentDomainList(newList);
                    setDomainName("");
                    setCurrentEditDomain("");
                    if (!isDomainListUpdate) {
                        setDomainListUpdate(true);
                    }
                } else {
                    setErrorMsg(t("このドメインが重複しています。"));
                }
            }
        }

        // eslint-disable-next-line
    }, [currentDomainList, domainName]);

    // ドメインリストの編集ボタン処理
    const editDomainIndexClick = (domain: string) => {
        setDomainName(domain);
        setCurrentEditDomain(domain);
        setIsVisibleDomainDel(true);
    };

    // ドメインリストの削除ボタン処理
    const deleteDomainClick = (val: string) => {
        const newList = currentDomainList.filter((domain) => domain.emailDomain !== val);
        setCurrentDomainList(newList);
        if (!isDomainListUpdate) {
            setDomainListUpdate(true);
        }
    };

    // 入力した値をドメイン名に設定する。
    const handleExceptionMailChange = (event: any) => {
        setExceptionMail(event.target.value);
    };

    // 追加ボタン活性・非活性変更
    useEffect(() => {
        if (isEmpty(exceptionMail)) {
            setExceptionAddDisabled(true);
        } else {
            setExceptionAddDisabled(false);
        }
        // eslint-disable-next-line
    }, [exceptionMail]);

    // メール追加ボタン処理
    const handleClickExcepitonMailAdd = useCallback(() => {
        if (!validEmail.test(exceptionMail)) {
            setErrorMsg(t("メールアドレスが正しくありません。"));
        } else {
            const emailLIst =
                currentEmailList.length > 0 ? currentEmailList.map((item) => item.email) : [];
            if (currentEditEmail === "") {
                if (!isDuplicate(exceptionMail, emailLIst, false)) {
                    currentEmailList.push({
                        id: -1,
                        email: exceptionMail,
                    });
                    setExceptionMail("");
                    if (!isEmailListUpdate) {
                        setEmailListUpdate(true);
                    }
                } else {
                    setErrorMsg(t("このメールは重複しています。"));
                }
            } else {
                var newList = [...currentEmailList];
                const index = newList.findIndex((item) => item.email === currentEditEmail);
                if (!isDuplicate(exceptionMail, emailLIst, true, index)) {
                    newList[index] = { ...newList[index], email: exceptionMail };
                    setCurrentEmailList(newList);
                    setExceptionMail("");
                    setCurrentEditEmail("");
                    if (!isEmailListUpdate) {
                        setEmailListUpdate(true);
                    }
                } else {
                    setErrorMsg(t("このメールは重複しています。"));
                }
            }
        }

        // eslint-disable-next-line
    }, [currentEmailList, exceptionMail, currentEditEmail]);

    const handleCancel = () => {
        setDomainName("");
        setExceptionMail("");
        setIsVisibleDomainDel(false);
        setIsVisibleEmailDel(false);
    };

    // EMAILリストの編集ボタン処理
    const editExceptionClick = (email: string) => {
        setExceptionMail(email);
        setCurrentEditEmail(email);
        setIsVisibleEmailDel(true);
    };

    // EMAILリストの削除ボタン処理
    const deleteExceptionClick = (val: string) => {
        const newList = currentEmailList.filter((exception) => exception.email !== val);
        setCurrentEmailList(newList);
        if (!isEmailListUpdate) {
            setEmailListUpdate(true);
        }
    };

    function isDuplicate(name: string, list: string[], isEditFlag: boolean, index?: number) {
        let isDuplicateFlag = false;
        const filteredList = list.filter((item) => item?.toLowerCase() === name?.toLowerCase());
        if (isEditFlag) {
            if (filteredList.length > 1) {
                isDuplicateFlag = true;
            } else if (filteredList.length === 1) {
                const currentIndex = list.findIndex((item) => item === name);
                if (index !== currentIndex) {
                    isDuplicateFlag = true;
                }
            }
        } else {
            if (filteredList.length > 0) {
                isDuplicateFlag = true;
            }
        }
        return isDuplicateFlag;
    }

    // 更新ボタン処理
    const handleClickUpdate = () => {
        if (isDomainListUpdate && userInfo) {
            const updatedDomainList =
                currentDomainList.length > 0
                    ? currentDomainList.map((domain) => domain.emailDomain)
                    : [];
            dispatch(UpdateAccessableEmailDomainList(updatedDomainList, Number(userInfo?.id)));
        }
        if (isEmailListUpdate && userInfo) {
            const updatedEmailList =
                currentEmailList.length > 0 ? currentEmailList.map((current) => current.email) : [];
            dispatch(UpdateAccessableEmailList(updatedEmailList, Number(userInfo?.id)));
        }
        setSuccessMsg(t("更新しました。"));
        ResetInput();
    };

    return (
        <div className={styles.root}>
            <div className={styles.infoDiv}>
                <div className={styles.titleDiv}>
                    <PuiTypography color="white">
                        アクセス許可するドメイン（登録が無い場合は、すべてのドメインのユーザーがアクセス可能）
                    </PuiTypography>
                </div>
                <div className={styles.domainDiv}>
                    <div className={styles.addDomainDiv}>
                        <div className={styles.addTextFieldDiv}>
                            <ThemeProvider theme={theme}>
                                <TextField
                                    color="secondary"
                                    id="indexInfo"
                                    className={classes.textfield}
                                    autoComplete="off"
                                    variant="outlined"
                                    value={domainName}
                                    onChange={handleDomainChange}
                                    placeholder={setMessageArgs(
                                        MSG_INPUT_REQUEST_INPUT,
                                        t("許可するドメイン")
                                    )}
                                />
                            </ThemeProvider>
                            {isVisibleDomainDel && (
                                <div className={styles.deleteIconDiv}>
                                    <DeleteIcon
                                        className={classes.deleteIcon}
                                        onClick={handleCancel}
                                    />
                                </div>
                            )}
                        </div>
                        {!isDomainAddDisabled ? (
                            <Button
                                size="small"
                                variant="outlined"
                                type="submit"
                                onClick={handleClickDomainAdd}
                                className={classes.addButton}
                                style={{
                                    background: "var(--studio-btn-bg)",
                                    color: "var(--white)",
                                }}
                            >
                                {isVisibleDomainDel ? t("更新") : t("追加")}
                            </Button>
                        ) : (
                            <Button
                                size="small"
                                variant="outlined"
                                type="submit"
                                onClick={handleClickDomainAdd}
                                className={classes.addButton}
                                style={{
                                    backgroundColor: "var(--button-bg)",
                                    color: "var(--loading-bg)",
                                }}
                            >
                                {isVisibleDomainDel ? t("更新") : t("追加")}
                            </Button>
                        )}
                    </div>
                    <div className={styles.listArea}>
                        {currentDomainList &&
                            currentDomainList.length > 0 &&
                            currentDomainList.map((current: AccessableEmailDomain, index) => (
                                <div className={styles.listItemDiv} key={index}>
                                    <div className={styles.textDiv}>
                                        <PuiTypography size={20} color={"white"}>
                                            {current.emailDomain}
                                        </PuiTypography>
                                    </div>
                                    <div className={styles.iconDiv}>
                                        <EditIcon
                                            className={classes.icon}
                                            onClick={() =>
                                                editDomainIndexClick(current.emailDomain)
                                            }
                                        />
                                    </div>
                                    <div className={styles.iconDiv}>
                                        <DeleteIcon
                                            className={classes.icon}
                                            onClick={() => deleteDomainClick(current.emailDomain)}
                                        />
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
                <div className={styles.titleDiv}>
                    <PuiTypography color="white">
                        アクセス許可するメールアドレス（上記許可ドメイン以外のユーザーで、特別にアクセスを許可するユーザーのメールアドレスを入力）
                    </PuiTypography>
                </div>

                <div className={styles.exceptionDiv}>
                    <div className={styles.addExceptionDiv}>
                        <ThemeProvider theme={theme}>
                            <div className={styles.addTextFieldDiv}>
                                <TextField
                                    color="secondary"
                                    id="indexInfo"
                                    className={classes.textfield}
                                    autoComplete="off"
                                    variant="outlined"
                                    value={exceptionMail}
                                    onChange={handleExceptionMailChange}
                                    placeholder={setMessageArgs(
                                        MSG_INPUT_REQUEST_INPUT,
                                        t("許可するメール")
                                    )}
                                />
                                {isVisibleEmailDel && (
                                    <div className={styles.deleteIconDiv}>
                                        <DeleteIcon
                                            className={classes.deleteIcon}
                                            onClick={handleCancel}
                                        />
                                    </div>
                                )}
                            </div>
                        </ThemeProvider>
                        {!isExceptionAddDisabled ? (
                            <Button
                                size="small"
                                variant="outlined"
                                type="submit"
                                onClick={handleClickExcepitonMailAdd}
                                className={classes.addButton}
                                style={{
                                    background: "var(--studio-btn-bg)",
                                    color: "var(--white)",
                                }}
                            >
                                {isVisibleEmailDel ? t("更新") : t("追加")}
                            </Button>
                        ) : (
                            <Button
                                size="small"
                                variant="outlined"
                                type="submit"
                                onClick={handleClickExcepitonMailAdd}
                                className={classes.addButton}
                                style={{
                                    backgroundColor: "var(--button-bg)",
                                    color: "var(--loading-bg)",
                                }}
                            >
                                {isVisibleEmailDel ? t("更新") : t("追加")}
                            </Button>
                        )}
                    </div>

                    <div className={styles.listArea}>
                        {currentEmailList &&
                            currentEmailList.length > 0 &&
                            currentEmailList.map((current: AccessableEmail, index) => (
                                <div className={styles.listItemDiv} key={index}>
                                    <div className={styles.textDiv}>
                                        <PuiTypography size={20} color={"white"}>
                                            {current.email}
                                        </PuiTypography>
                                    </div>
                                    <div className={styles.iconDiv}>
                                        <EditIcon
                                            className={classes.icon}
                                            onClick={() => editExceptionClick(current.email)}
                                        />
                                    </div>
                                    <div className={styles.iconDiv}>
                                        <DeleteIcon
                                            className={classes.icon}
                                            onClick={() => deleteExceptionClick(current.email)}
                                        />
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
            </div>
            <div className={styles.btnDiv}>
                <ThemeProvider theme={theme}>
                    {isEmailListUpdate || isDomainListUpdate ? (
                        <Button
                            type="submit"
                            onClick={handleClickUpdate}
                            color="secondary"
                            className={styles.addButton}
                            style={{
                                color: "white",
                                backgroundColor: "var(--studio-btn-bg)",
                                fontSize: "16px",
                                fontWeight: "bold",
                                fontFamily: "noto-sans-cjk-jp,sans-serif",
                            }}
                        >
                            {t("更新")}
                        </Button>
                    ) : (
                        <Button
                            disabled={!isEmailListUpdate || !isDomainListUpdate}
                            type="submit"
                            onClick={handleClickUpdate}
                            color="secondary"
                            className={styles.addButton}
                            style={{
                                color: "var(--loading-bg)",
                                fontSize: "16px",
                                fontFamily: "noto-sans-cjk-jp,sans-serif",
                                backgroundColor: "var(--button-bg)",
                            }}
                        >
                            {t("更新")}
                        </Button>
                    )}
                </ThemeProvider>
            </div>
            <PuiMessage
                open={errorMsg !== "" ? true : false}
                type="error"
                title={errorMsg}
                duration={5000}
                onClose={closeErrorMsg}
            />
            <PuiMessage
                open={successMsg !== "" ? true : false}
                type="success"
                title={successMsg}
                duration={5000}
                onClose={closeSuccessMsg}
            />
        </div>
    );
}
